// =============================================================================
// domain/userTags.ts — creating, renaming, recolouring and removing a user tag.
// -----------------------------------------------------------------------------
// The registry itself — its shape and its parser — lives in `facets.ts`, because
// that is where it is READ: the filter drawer builds its options from it and the
// trade form renders a trade's chips from it. This file is the WRITE half, kept
// apart because the two have different hazards and only one of them is
// destructive.
//
// ── ⚠ WHY DELETE DOES NOT TOUCH THE TRADES ──
//
// The old client's delete strips the tag id out of every trade in memory and
// saves the lot. It can, because the whole book is in memory there. Here the
// trades are rows in a database, and doing the same thing means a bulk write
// over every trade that carries the tag — hundreds of read-modify-writes, each
// one an opportunity to clobber a column this screen does not own. That is the
// exact hazard the write rules on this port were written for.
//
// It is also unnecessary. `tradeTagIds` already filters a trade's ids down to
// the ones the registry still knows, so an id whose tag is gone renders nowhere,
// filters nothing, and cannot come back: a re-created tag gets a fresh id.
//
// ▶ So removing a tag is one preference write, and the orphaned ids are inert.
// The screen says what happens in plain words rather than leaving it to be
// discovered — the same thing the mistake-tag editor beside it does.
// =============================================================================

import type { UserTag } from './facets';

/**
 * The palette a new tag cycles through, in order.
 *
 * ⚠ THE OLD CLIENT'S TEN, IN ITS ORDER, and picked by `length % 10` so the first
 * ten tags a trader makes are all different colours. Copied rather than
 * re-chosen: a user's existing tags carry these exact values, and a new palette
 * would make a tag created today sit oddly beside one created last month.
 */
export const TAG_COLORS: readonly string[] = [
  '#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444',
  '#ec4899', '#8b5cf6', '#14b8a6', '#f97316', '#64748b',
];

/** The colour a tag gets when the trader does not choose one. */
export const nextTagColor = (count: number): string =>
  TAG_COLORS[Math.abs(count) % TAG_COLORS.length] as string;

/** ⚠ Long enough for a phrase, short enough to stay a chip. The old client's. */
export const TAG_NAME_MAX = 40;

/**
 * Add a tag, or return the registry unchanged when it would be a duplicate.
 *
 * ⚠ CASE-INSENSITIVE DE-DUPE, which is the old client's rule and the right one:
 * "Chased" and "chased" are one habit, and a book with both splits every count
 * that mentions it between two chips that look identical on screen.
 *
 * ⚠ RETURNS THE SAME ARRAY when nothing changed, so a caller can skip the write
 * rather than persisting an identical registry and bumping `updated_at`.
 */
export function addUserTag(
  registry: readonly UserTag[], name: string, id: string, color?: string,
): readonly UserTag[] {
  const trimmed = name.trim().slice(0, TAG_NAME_MAX);
  if (!trimmed) return registry;
  const lower = trimmed.toLowerCase();
  if (registry.some((t) => t.name.trim().toLowerCase() === lower)) return registry;
  return [...registry, { id, name: trimmed, color: color || nextTagColor(registry.length) }];
}

/**
 * Rename and/or recolour one tag.
 *
 * ⚠ AN EMPTY NAME IS IGNORED, not saved. The name box is a live input on a row
 * that already exists, so a user who selects-all and pauses before typing has an
 * empty box for a moment — committing that would leave a chip labelled by its
 * own id everywhere it is applied.
 *
 * ⚠ A RENAME THAT COLLIDES with another tag is also ignored, for the reason
 * `addUserTag` de-dupes: two chips reading "chased" is worse than a rename that
 * quietly did not take, and the screen reports it rather than staying silent.
 */
export function editUserTag(
  registry: readonly UserTag[], id: string, patch: { name?: string; color?: string },
): readonly UserTag[] {
  const target = registry.find((t) => t.id === id);
  if (!target) return registry;

  let name = target.name;
  if (patch.name != null) {
    const trimmed = patch.name.trim().slice(0, TAG_NAME_MAX);
    if (!trimmed) return registry;
    const lower = trimmed.toLowerCase();
    if (registry.some((t) => t.id !== id && t.name.trim().toLowerCase() === lower)) return registry;
    name = trimmed;
  }
  const color = patch.color ?? target.color;
  if (name === target.name && color === target.color) return registry;
  return registry.map((t) => (t.id === id ? { ...t, name, color } : t));
}

/**
 * Remove a tag from the registry.
 *
 * ⚠ THE TRADES ARE NOT TOUCHED — see the header. The label disappears from every
 * trade that carried it, because nothing renders an id the registry has
 * forgotten, but no trade row is written.
 */
export function removeUserTag(
  registry: readonly UserTag[], id: string,
): readonly UserTag[] {
  return registry.some((t) => t.id === id) ? registry.filter((t) => t.id !== id) : registry;
}

/**
 * Back to the shape `preferences.customTags` stores.
 *
 * ⚠ PLAIN OBJECTS, and only the three fields. The parser tolerates extra keys on
 * the way in; writing them back out would persist whatever a hand-edited row or
 * an older version happened to carry, and this app would then own it.
 */
export function toStoredTags(registry: readonly UserTag[]): { id: string; name: string; color: string }[] {
  return registry.map((t) => ({ id: t.id, name: t.name, color: t.color }));
}
